import { QueryClient, QueryFilters, QueryKey } from '@tanstack/react-query'
import { Style } from '../../core/models/entities/Style'
import { PagedResponse } from '../models/responses/pagedResponse'
import { actAsCacheKey } from '../../contexts/actAsStore'

/**
 * In-memory patch of the cached style lists after a successful user-style
 * upload (enviroment.uploadListPatchEnabled). Replaces today's
 * refetchQueries(["styles"], type "all") - one GET per cached entry, awaited
 * before the dialog closes - with: prepend the new style into every cached
 * UNFILTERED first-page list of the current act-as scope, then mark all
 * ["styles"] entries stale without refetching so other screens revalidate
 * lazily.
 */

interface InfiniteListLike<T extends object> {
  pages: PagedResponse<T>[]
  pageParams: unknown[]
}

function isPagedResponse<T extends object>(
  value: unknown
): value is PagedResponse<T> {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as PagedResponse<T>).results)
  )
}

function isInfiniteList<T extends object>(
  value: unknown
): value is InfiniteListLike<T> {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as InfiniteListLike<T>).pages) &&
    Array.isArray((value as InfiniteListLike<T>).pageParams)
  )
}

const PAGE_INDEX = /[?&]pageIndex=(\d+)(?:&|$)/

/**
 * Style list keys are ["styles", <act-as scope>, <toQueryParam string>].
 * Only lists where "the new style first" is the truth are patched:
 * - no search term / brand filter (the new style may legitimately fall
 *   outside them);
 * - no sortByField (prepending would break the order the user chose);
 * - page 1 (or no pageIndex) - a paged admin list caches every visited page
 *   under its own key, and the new style must not appear first on page 2, 3...
 * Everything else, detail keys (["styles", id]) included, is left to the
 * lazy invalidation.
 */
export function isUnfilteredStyleListKey(queryKey: QueryKey): boolean {
  if (!Array.isArray(queryKey) || queryKey.length !== 3) return false
  if (queryKey[0] !== 'styles') return false
  const params = queryKey[2]
  if (typeof params !== 'string') return false
  if (params.includes('searchTerm=') || params.includes('filterByField=')) {
    return false
  }
  if (params.includes('sortByField=')) return false
  const pageIndex = PAGE_INDEX.exec(params)
  return !pageIndex || pageIndex[1] === '1'
}

/**
 * Pure updater: the cached value with `style` as the first row of the first
 * page (any existing row with the same id is dropped first, so a second
 * patch never duplicates it). Unknown shapes are returned unchanged.
 */
export function prependStyleToCachedList(old: unknown, style: Style): unknown {
  if (isInfiniteList<Style>(old)) {
    if (old.pages.length === 0) return old
    const pages = old.pages.map((page, index) => {
      const results = (page.results ?? []).filter((s) => s.id !== style.id)
      return {
        ...page,
        results: index === 0 ? [style, ...results] : results,
      }
    })
    return { ...old, pages }
  }
  if (isPagedResponse<Style>(old)) {
    const results = old.results.filter((s) => s.id !== style.id)
    return { ...old, results: [style, ...results] }
  }
  return old
}

/**
 * A list whose fetch is in flight when the patch lands (the chooser's
 * fetchNextPage, or a background refetch) loses the patch: react-query
 * rebuilds an infinite query's pages from the snapshot the fetch started
 * with, and a paged query simply replaces its data. Re-apply the prepend
 * once each such query settles (success or error) - one cache subscription,
 * no request; the dedupe in prependStyleToCachedList keeps it idempotent
 * when the server answer already contains the style. Returns the number of
 * in-flight lists being watched (for tests/diagnostics).
 */
function reapplyAfterInFlightFetches(
  queryClient: QueryClient,
  filters: QueryFilters,
  style: Style
): number {
  const cache = queryClient.getQueryCache()
  const pending = new Set(
    cache
      .findAll(filters)
      .filter((query) => query.state.fetchStatus === 'fetching')
      .map((query) => query.queryHash)
  )
  if (pending.size === 0) return 0
  const unsubscribe = cache.subscribe((event) => {
    if (!event || !pending.has(event.query.queryHash)) return
    if (event.type === 'removed') {
      pending.delete(event.query.queryHash)
    } else if (event.query.state.fetchStatus === 'idle') {
      pending.delete(event.query.queryHash)
      queryClient.setQueryData<unknown>(event.query.queryKey, (old: unknown) =>
        prependStyleToCachedList(old, style)
      )
    }
    if (pending.size === 0) unsubscribe()
  })
  return pending.size
}

/**
 * Prepend `style` into every cached unfiltered first-page style list of the
 * current act-as scope (active AND inactive - the chooser's infinite query
 * has refetchOnMount false, so an inactive copy is served as-is on the next
 * mount and must be patched too), re-apply for lists whose fetch is in
 * flight, then mark every ["styles"] entry stale without a refetch. Returns
 * the number of list entries patched (for tests/diagnostics). Never issues
 * a request.
 */
export function patchStyleListsWithNewStyle(
  queryClient: QueryClient,
  style: Style
): number {
  const filters: QueryFilters = {
    queryKey: ['styles', actAsCacheKey()],
    type: 'all',
    predicate: (query) => isUnfilteredStyleListKey(query.queryKey),
  }
  const patched = queryClient.setQueriesData<unknown>(filters, (old: unknown) =>
    prependStyleToCachedList(old, style)
  )
  reapplyAfterInFlightFetches(queryClient, filters, style)
  queryClient.invalidateQueries({ queryKey: ['styles'], refetchType: 'none' })
  return patched.length
}
